import { useState, useEffect, FormEvent, useMemo } from 'react';
import { client } from '../../sdkAdapter';
import {
    CatalogImageRecord,
    CategoryRecord,
    extractApiErrorMessage,
    extractCategoryTree,
    flattenCategoryTree,
    sanitizeSlug,
    toBoolean,
} from './catalogApi';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import MediaUploader from "../common/MediaUploader";
import CatalogModalFrame from './CatalogModalFrame';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from './catalogAlerts';
import { useSeoOptions } from '../../hooks/useSeoOptions';

interface CategoryFormState {
    name: string;
    label: string;
    slug: string;
    description: string;
    parent_id: number | null;
    sort: string;
    active: boolean;
    top: boolean;
    seo_id: string;
    image_id?: number;
}

interface ValidatedCategoryForm {
    name: string;
    label: string;
    slug: string;
    description?: string;
    parent_id: number | null;
    sort?: number;
    active: boolean;
    top: boolean;
    seo_id?: number;
    image_id?: number;
}

const emptyForm = (): CategoryFormState => ({
    name: '',
    label: '',
    slug: '',
    description: '',
    parent_id: null,
    sort: '0',
    active: true,
    top: false,
    seo_id: '',
    image_id: undefined,
});

const syncCategoryTextFields = (previous: CategoryFormState, nextNameRaw: string): CategoryFormState => {
    const nextName = nextNameRaw;
    const previousNameSlug = sanitizeSlug(previous.name);
    const nextNameSlug = sanitizeSlug(nextName);
    const shouldAutofillSlug = !previous.slug.trim() || sanitizeSlug(previous.slug) === previousNameSlug;
    const shouldAutofillLabel = !previous.label.trim() || previous.label.trim() === previous.name.trim();

    return {
        ...previous,
        name: nextName,
        label: shouldAutofillLabel ? nextName : previous.label,
        slug: shouldAutofillSlug ? nextNameSlug : previous.slug,
    };
};

const getFullUrl = (url?: string | null): string => {
    if (!url) {
        return '';
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return encodeURI(url);
    }

    const baseUrl = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');
    const cleanUrl = url.replace(/^\//, '');

    return encodeURI(`${baseUrl}/${cleanUrl}`);
};

const getImageSource = (image?: CatalogImageRecord | null): string => {
    const path = image?.path || image?.thumb_r || image?.thumb_m || image?.thumb_s || '';
    return getFullUrl(path);
};

const isDescendant = (parentId: number, childId: number, flatList: CategoryRecord[]): boolean => {
    const child = flatList.find((item) => item.id === childId);
    if (!child) {
        return false;
    }

    if (child.parent_id === parentId) {
        return true;
    }

    if (typeof child.parent_id === 'number') {
        return isDescendant(parentId, child.parent_id, flatList);
    }

    return false;
};

const validateCategoryForm = (
    form: CategoryFormState,
    editingId: number | null,
    flatCategories: CategoryRecord[],
): { valid: true; data: ValidatedCategoryForm; } | { valid: false; message: string; } => {
    const name = form.name.trim();
    const label = form.label.trim() || name;
    const slugSource = form.slug.trim() || name;
    const slug = sanitizeSlug(slugSource);
    const description = form.description.trim();
    const sortValue = form.sort.trim();
    const seoValue = form.seo_id.trim();

    if (!name) {
        return { valid: false, message: 'Category name is required.' };
    }

    if (form.slug.trim() && !slug) {
        return { valid: false, message: 'Slug can only contain letters, numbers, and hyphens.' };
    }

    if (editingId !== null && form.parent_id === editingId) {
        return { valid: false, message: 'A category cannot be its own parent.' };
    }

    if (editingId !== null && typeof form.parent_id === 'number' && isDescendant(editingId, form.parent_id, flatCategories)) {
        return { valid: false, message: 'Cannot move a parent category into its own child.' };
    }

    let sort: number | undefined;
    if (sortValue) {
        sort = Number(sortValue);
        if (!Number.isInteger(sort) || sort < 0) {
            return { valid: false, message: 'Sort order must be a whole number greater than or equal to 0.' };
        }
    }

    let seoId: number | undefined;
    if (seoValue) {
        seoId = Number(seoValue);
        if (!Number.isInteger(seoId) || seoId <= 0) {
            return { valid: false, message: 'SEO ID must be a whole number greater than 0.' };
        }
    }

    return {
        valid: true,
        data: {
            name,
            label,
            slug,
            ...(description ? { description } : {}),
            ...(typeof sort === 'number' ? { sort } : {}),
            ...(typeof seoId === 'number' ? { seo_id: seoId } : {}),
            ...(typeof form.image_id === 'number' ? { image_id: form.image_id } : {}),
            parent_id: form.parent_id,
            active: form.active,
            top: form.top,
        }
    };
};

export default function CatalogCategories() {
    const [categories, setCategories] = useState<CategoryRecord[]>([]);
    const [imageUrlsById, setImageUrlsById] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<CategoryFormState>(emptyForm);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeId, setActiveId] = useState<number | null>(null);
    const { seoOptions, seoOptionsLoading } = useSeoOptions();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await client.getApiCatalogCategories() as any;
            setCategories(extractCategoryTree(res));
        } catch (err) {
            console.error('Failed to fetch categories:', err);
            setCategories([]);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to load categories'));
        } finally {
            setLoading(false);
        }
    };

    const organizedCategories = useMemo(() => flattenCategoryTree(categories), [categories]);

    useEffect(() => {
        const missingImageIds = organizedCategories
            .map((category) => category.image_id)
            .filter((id): id is number => typeof id === 'number' && id > 0)
            .filter((id) => !imageUrlsById[id]);

        if (missingImageIds.length === 0) {
            return;
        }

        const uniqueIds = Array.from(new Set(missingImageIds));

        const fetchImageUrls = async () => {
            try {
                const responses = await Promise.all(
                    uniqueIds.map((id) => client.getApiMediaFilesId({ id: String(id) }) as any)
                );

                const nextEntries: Array<[number, string]> = responses
                    .map((res, index) => {
                        const responseData = res?.data?.data;
                        const file = Array.isArray(responseData) ? responseData[0] : responseData;
                        const src = getImageSource(file);
                        return [uniqueIds[index], src] as [number, string];
                    })
                    .filter(([, src]) => Boolean(src));

                if (nextEntries.length > 0) {
                    setImageUrlsById((prev) => ({
                        ...prev,
                        ...Object.fromEntries(nextEntries),
                    }));
                }
            } catch (err) {
                console.error('Failed to resolve category image URLs:', err);
            }
        };

        fetchImageUrls();
    }, [organizedCategories, imageUrlsById]);

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as number);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        setActiveId(null);

        if (!over || active.id === over.id) {
            return;
        }

        const draggedCat = organizedCategories.find((cat) => cat.id === active.id);
        const overCat = organizedCategories.find((cat) => cat.id === over.id);

        if (!draggedCat || !overCat) {
            return;
        }

        if (isDescendant(draggedCat.id, overCat.id, organizedCategories)) {
            await showValidationAlert('Cannot move a parent category into its own child');
            return;
        }

        try {
            await client.postApiCatalogCategoryUpdate({
                postApiCatalogCategoryUpdateRequest: {
                    id: draggedCat.id,
                    name: draggedCat.name,
                    label: draggedCat.label || undefined,
                    slug: draggedCat.slug || undefined,
                    parent_id: overCat.id,
                    description: draggedCat.description || undefined,
                    top: toBoolean(draggedCat.top),
                    sort: typeof draggedCat.sort === 'number' ? draggedCat.sort : undefined,
                    active: toBoolean(draggedCat.active ?? true),
                    image_id: draggedCat.image_id || draggedCat.files?.id || undefined,
                    seo_id: draggedCat.seo_id ? String(draggedCat.seo_id) : (draggedCat.seo as any)?.id ? String((draggedCat.seo as any).id) : undefined,
                }
            });

            await fetchCategories();
            showSuccessToast(`Moved ${draggedCat.name} under ${overCat.name}`);
        } catch (err: any) {
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to move category'));
        }
    };

    const handleEdit = (category: CategoryRecord) => {
        setEditingId(category.id);
        setForm({
            name: category.name || '',
            label: category.label || '',
            slug: category.slug || '',
            description: category.description || '',
            parent_id: typeof category.parent_id === 'number' ? category.parent_id : null,
            sort: typeof category.sort === 'number' ? String(category.sort) : '0',
            active: toBoolean(category.active ?? true),
            top: toBoolean(category.top ?? false),
            seo_id: category.seo_id ? String(category.seo_id) : String((category.seo as any)?.id || ''),
            image_id: category.image_id || category.files?.id,
        });
        setIsModalOpen(true);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(emptyForm());
        setIsModalOpen(false);
    };

    const createCategory = async () => {
        const validated = validateCategoryForm(form, editingId, organizedCategories);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogCategoryCreate({
                postApiCatalogCategoryCreateRequest: validated.data
            });
            handleCancelEdit();
            await fetchCategories();
            showSuccessToast('Category created successfully');
        } catch (err: any) {
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to create category'));
        } finally {
            setSubmitting(false);
        }
    };

    const updateCategory = async () => {
        if (editingId === null) {
            return;
        }

        const validated = validateCategoryForm(form, editingId, organizedCategories);
        if (!validated.valid) {
            await showValidationAlert(validated.message);
            return;
        }

        setSubmitting(true);
        try {
            await client.postApiCatalogCategoryUpdate({
                postApiCatalogCategoryUpdateRequest: {
                    id: editingId,
                    ...validated.data,
                    seo_id: typeof validated.data.seo_id === 'number' ? String(validated.data.seo_id) : undefined,
                }
            });
            handleCancelEdit();
            await fetchCategories();
            showSuccessToast('Category updated successfully');
        } catch (err: any) {
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to update category'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (editingId !== null) {
            await updateCategory();
            return;
        }

        await createCategory();
    };

    const handleDelete = async (id: number) => {
        const isConfirmed = await confirmAction({
            text: 'This category will be permanently deleted.',
            confirmButtonText: 'Delete Category'
        });

        if (!isConfirmed) {
            return;
        }

        try {
            await client.postApiCatalogCategoryDelete({
                postApiCatalogCategoryDeleteRequest: { id }
            });

            if (editingId === id) {
                handleCancelEdit();
            }

            await fetchCategories();
            showSuccessToast('Category deleted successfully');
        } catch (err: any) {
            console.error(err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to delete category'));
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Categories</h2>
                <button
                    onClick={() => {
                        handleCancelEdit();
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-brand-600 active:scale-95"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Category
                </button>
            </div>

            <CatalogModalFrame
                isOpen={isModalOpen}
                onClose={handleCancelEdit}
                title={editingId !== null ? 'Edit Category' : 'Create Category'}
                className="max-w-2xl"
                formId="catalog-category-form"
                submitLabel={submitting ? 'Processing...' : editingId !== null ? 'Update Category' : 'Create Category'}
                submitDisabled={submitting}
            >
                <form id="catalog-category-form" onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Category Image</label>
                        <MediaUploader
                            onUpload={(ids) => setForm((prev) => ({ ...prev, image_id: ids[0] }))}
                            initialImages={form.image_id ? [form.image_id] : []}
                            multiple={false}
                            folder="categories"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                value={form.name}
                                onChange={(e) => setForm((prev) => syncCategoryTextFields(prev, e.target.value))}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                placeholder="e.g. Electronics"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Label</label>
                            <input
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                placeholder="Display Label"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Slug</label>
                            <input
                                value={form.slug}
                                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                maxLength={100}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                placeholder="Auto-generated if left blank"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Parent Category</label>
                            <select
                                value={form.parent_id || ''}
                                onChange={(e) => setForm({ ...form, parent_id: e.target.value ? Number(e.target.value) : null })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                            >
                                <option value="">None (Top Level)</option>
                                {organizedCategories
                                    .filter((cat) => {
                                        if (cat.id === editingId) {
                                            return false;
                                        }
                                        if (editingId !== null && isDescendant(editingId, cat.id, organizedCategories)) {
                                            return false;
                                        }
                                        return true;
                                    })
                                    .map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.level && cat.level > 0 ? '— '.repeat(cat.level) : ''}{cat.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Description</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                            placeholder="Category description"
                            rows={3}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Sort Order</label>
                            <input
                                type="number"
                                min={0}
                                value={form.sort}
                                onChange={(e) => setForm({ ...form, sort: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">SEO ID</label>
                            <select
                                value={form.seo_id}
                                onChange={(e) => setForm({ ...form, seo_id: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                            >
                                <option value="">
                                    {seoOptionsLoading ? 'Loading SEO records...' : 'Select SEO record'}
                                </option>
                                {seoOptions.map((seo) => (
                                    <option key={seo.id} value={String(seo.id)}>
                                        #{seo.id} - {seo.meta_title?.trim() || seo.url}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                            <select
                                value={form.active ? 'true' : 'false'}
                                onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer group rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                        <input
                            type="checkbox"
                            checked={form.top}
                            onChange={(e) => setForm({ ...form, top: e.target.checked })}
                            className="w-4 h-4 text-brand-500 border-gray-300 rounded focus:ring-brand-500"
                        />
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300 group-hover:text-brand-500 transition-colors">Top Category</span>
                    </label>
                </form>
            </CatalogModalFrame>

            <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                <div className="p-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Existing Categories</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {loading ? 'Loading category tree...' : `${organizedCategories.length} categor${organizedCategories.length === 1 ? 'y' : 'ies'} loaded from the tree response.`}
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-10">&nbsp;</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider w-20">Image</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Name</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Slug</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Parent</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Sort</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Top</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Status</TableCell>
                                    <TableCell isHeader className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-end">Actions</TableCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 font-medium">
                                            Loading categories...
                                        </TableCell>
                                    </TableRow>
                                ) : categories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 font-medium">
                                            No categories found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <SortableContext items={organizedCategories.map((category) => category.id)} strategy={verticalListSortingStrategy}>
                                        {organizedCategories.map((category) => (
                                            <DraggableRow
                                                key={category.id}
                                                category={category}
                                                editingId={editingId}
                                                categories={organizedCategories}
                                                imageUrlsById={imageUrlsById}
                                                activeId={activeId}
                                                onEdit={handleEdit}
                                                onDelete={handleDelete}
                                            />
                                        ))}
                                    </SortableContext>
                                )}
                            </TableBody>
                        </Table>
                    </DndContext>
                </div>
            </div>
        </div>
    );
}

interface DraggableRowProps {
    category: CategoryRecord;
    editingId: number | null;
    categories: CategoryRecord[];
    imageUrlsById: Record<number, string>;
    activeId: number | null;
    onEdit: (category: CategoryRecord) => void;
    onDelete: (id: number) => void;
}

function DraggableRow({ category, editingId, categories, imageUrlsById, activeId, onEdit, onDelete }: DraggableRowProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: category.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const categoryImage =
        getImageSource(category.files) ||
        getImageSource(category.images?.[0] || null) ||
        (category.image_id ? imageUrlsById[category.image_id] || '' : '');

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`group transition-all hover:bg-gray-50 dark:hover:bg-gray-800/50 ${editingId === category.id ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''} ${activeId === category.id ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}
        >
            <td className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-brand-500 transition-colors"
                    title="Drag to reorder or change parent"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                </button>
            </td>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                {categoryImage ? (
                    <img
                        src={categoryImage}
                        alt={category.name}
                        className="w-10 h-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700"
                    />
                ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs text-gray-400 font-bold border border-gray-200 dark:border-gray-700">
                        {category.name.substring(0, 2).toUpperCase()}
                    </div>
                )}
            </TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                <span style={{ paddingLeft: `${(category.level || 0) * 24}px` }} className={`flex items-center font-bold text-gray-700 dark:text-gray-200 ${(category.level || 0) > 0 ? 'text-sm' : ''}`}>
                    {(category.level || 0) > 0 && <span className="text-gray-300 mr-2 font-light">└─</span>}
                    <span>
                        {category.name}
                        {category.label && category.label !== category.name ? (
                            <span className="block mt-1 text-xs font-medium text-gray-400">{category.label}</span>
                        ) : null}
                    </span>
                </span>
            </TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 font-mono text-xs text-gray-500">{category.slug || '-'}</TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
                {category.parent_id ? (
                    <span className="text-xs font-semibold px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                        {categories.find((item) => item.id === category.parent_id)?.name || '-'}
                    </span>
                ) : (
                    <span className="text-xs text-gray-400">-</span>
                )}
            </TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 text-center font-bold text-gray-600 dark:text-gray-400">
                {typeof category.sort === 'number' ? category.sort : 0}
            </TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 text-center">
                {toBoolean(category.top) ? (
                    <Badge color="info" size="sm">Top</Badge>
                ) : (
                    <Badge color="light" size="sm">Sub</Badge>
                )}
            </TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 text-center">
                <Badge color={toBoolean(category.active) ? 'success' : 'error'} size="sm">
                    {toBoolean(category.active) ? 'Active' : 'Inactive'}
                </Badge>
            </TableCell>
            <TableCell className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 text-end">
                <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={() => onEdit(category)}
                        className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-brand-500 hover:border-brand-200 shadow-sm transition-all active:scale-95"
                        title="Edit Category"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => onDelete(category.id)}
                        className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-red-500 hover:border-red-200 shadow-sm transition-all active:scale-95"
                        title="Delete Category"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </TableCell>
        </tr>
    );
}
